/**
 * Convierte texto/JSON pegado en bloques del builder de formularios.
 *
 * Formato texto (una directiva por línea, "#Tipo - Texto"):
 *   #Título 2 - Sobre la marca
 *   #Pregunta simple - ¿Cuándo nació la marca?
 *   #Checkbox - Elige varias palabras
 *   - Innovación
 *   - Cercanía
 *   #Desplegable - Tamaño | Pequeña | Mediana | Grande     (opciones inline con "|")
 *   #Pregunta simple* - ¿Nombre de la marca?                (el * tras el tipo la marca obligatoria)
 *
 * Formato JSON: [{ "tipo": "checkbox", "titulo": "...", "opciones": ["a", "b"], "obligatorio": true }]
 */

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ALIASES = {
  input: ["pregunta simple", "pregunta corta", "simple", "texto", "campo de texto", "campo", "input", "respuesta corta"],
  area_texto: ["pregunta larga", "pregunta abierta", "area de texto", "area texto", "texto largo", "textarea", "larga", "area_texto"],
  opciones_checkbox: ["checkbox", "checkboxes", "opciones checkbox", "opciones", "multiple", "seleccion multiple", "opciones_checkbox"],
  desplegable: ["desplegable", "select", "lista", "dropdown", "seleccion unica"],
  archivo: ["archivo", "archivos", "subida de archivos", "subida", "adjunto", "adjuntos", "file"],
  titulo: ["titulo", "encabezado", "heading", "seccion"],
  parrafo: ["parrafo", "descripcion", "nota", "explicacion", "texto informativo"],
  divisor: ["divisor", "separador", "linea"],
  paginacion: ["pagina", "paginacion", "salto de pagina", "nueva pagina"],
  consentimiento_privacidad: ["consentimiento", "consentimiento privacidad", "privacidad", "consentimiento_privacidad"],
};

const ALIAS_TO_TIPO = {};
Object.entries(ALIASES).forEach(([tipo, list]) => list.forEach((a) => { ALIAS_TO_TIPO[norm(a)] = tipo; }));

let counter = 0;
const newId = () => `blk_${Date.now()}_${(counter++).toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const cleanOpts = (arr) =>
  (arr || [])
    .map((o) => (typeof o === "string" ? o : o?.label ?? ""))
    .map((s) => String(s).trim())
    .filter(Boolean)
    .map((label) => ({ label }));

/** Resuelve el nombre de tipo (con posible nivel de título). Devuelve { tipo, nivel } o null. */
function resolveTipo(raw) {
  let key = norm(raw);
  let nivel = 1;
  const m = key.match(/^(?:titulo|h)\s*([1-6])$/);
  if (m) {
    nivel = parseInt(m[1], 10);
    key = "titulo";
  }
  const tipo = ALIAS_TO_TIPO[key];
  return tipo ? { tipo, nivel } : null;
}

const OBLIGATORIABLES = ["input", "area_texto", "opciones_checkbox", "desplegable", "archivo"];

function buildBlock(tipo, { texto = "", opciones = [], nivel = 1, extra = {}, obligatorio = false } = {}) {
  const b = buildBlockBase(tipo, { texto, opciones, nivel, extra });
  if (b && obligatorio && OBLIGATORIABLES.includes(tipo)) b.obligatorio = true;
  return b;
}

function buildBlockBase(tipo, { texto, opciones, nivel, extra }) {
  const id = newId();
  switch (tipo) {
    case "titulo":
      return { id, tipo, nivel, texto };
    case "parrafo":
      return { id, tipo, texto };
    case "input":
      return { id, tipo, titulo: texto, placeholder: extra.placeholder || "" };
    case "area_texto":
      return { id, tipo, titulo: texto, placeholder: extra.placeholder || "" };
    case "archivo":
      return { id, tipo, titulo: texto };
    case "opciones_checkbox":
    case "desplegable":
      return { id, tipo, titulo: texto, opciones: opciones.length ? opciones : [{ label: "" }] };
    case "divisor":
      return { id, tipo, altura_arriba: 16, altura_abajo: 16, estilo: "solid", grosor: 1 };
    case "paginacion":
      return { id, tipo };
    case "consentimiento_privacidad":
      return {
        id,
        tipo,
        titulo: texto || "He leído y acepto la política de privacidad",
        texto_enlace: "política de privacidad",
        url_politica: extra.url_politica || "",
      };
    default:
      return null;
  }
}

function parseJson(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (data && !Array.isArray(data) && Array.isArray(data.bloques)) data = data.bloques;
  if (data && !Array.isArray(data) && Array.isArray(data.preguntas)) data = data.preguntas;
  if (!Array.isArray(data)) return null;
  const bloques = [];
  const errores = [];
  data.forEach((item, i) => {
    const r = resolveTipo(item?.tipo);
    if (!r) {
      errores.push(`Elemento ${i + 1}: tipo "${item?.tipo ?? ""}" no reconocido`);
      return;
    }
    const nivel = Number.isInteger(item.nivel) ? Math.min(6, Math.max(1, item.nivel)) : r.nivel;
    const b = buildBlock(r.tipo, {
      texto: String(item.titulo ?? item.texto ?? "").trim(),
      opciones: cleanOpts(item.opciones),
      nivel,
      extra: item,
      obligatorio: item.obligatorio === true || item.obligatoria === true,
    });
    if (b) bloques.push(b);
  });
  return { bloques, errores };
}

function parseTexto(text) {
  const bloques = [];
  const errores = [];
  let current = null; // bloque con opciones que acepta líneas "- opción"

  text.split(/\r?\n/).forEach((rawLine, i) => {
    const line = rawLine.trim();
    if (!line) return;

    if (line.startsWith("#")) {
      current = null;
      const body = line.replace(/^#+\s*/, "");
      const sep = body.search(/\s[-–—:]\s|:\s/);
      const head = sep === -1 ? body : body.slice(0, sep);
      const rest = sep === -1 ? "" : body.slice(sep).replace(/^\s*[-–—:]\s*/, "");
      const obligatorio = /\*\s*$/.test(head) || /\(\s*obligatori[ao]\s*\)\s*$/i.test(head);
      const r = resolveTipo(head.replace(/\*\s*$/, "").replace(/\(\s*obligatori[ao]\s*\)\s*$/i, ""));
      if (!r) {
        errores.push(`Línea ${i + 1}: tipo "${head}" no reconocido`);
        return;
      }
      const parts = rest.split("|").map((s) => s.trim());
      const hasOpts = r.tipo === "opciones_checkbox" || r.tipo === "desplegable";
      const b = buildBlock(r.tipo, {
        texto: parts[0] || "",
        opciones: hasOpts ? cleanOpts(parts.slice(1)) : [],
        nivel: r.nivel,
        obligatorio,
      });
      if (b) {
        bloques.push(b);
        if (hasOpts) current = b;
      }
      return;
    }

    const opt = line.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (opt && current) {
      const label = opt[1].trim();
      if (label) {
        const base = current.opciones.length === 1 && !current.opciones[0].label ? [] : current.opciones;
        current.opciones = [...base, { label }];
      }
      return;
    }
    errores.push(`Línea ${i + 1}: ignorada («${line.slice(0, 40)}${line.length > 40 ? "…" : ""}»)`);
  });
  return { bloques, errores };
}

/** @returns {{ bloques: object[], errores: string[] }} */
export function parseFormQuestions(input) {
  const text = String(input || "").trim();
  if (!text) return { bloques: [], errores: [] };
  if (text.startsWith("[") || text.startsWith("{")) {
    const j = parseJson(text);
    if (j) return j;
    return { bloques: [], errores: ["JSON no válido"] };
  }
  return parseTexto(text);
}

export const FORM_QUESTIONS_AI_PROMPT = `Devuélveme el formulario en este formato, una directiva por línea, dentro de un bloque de código y sin explicaciones:

#Título 2 - Texto del título        (niveles 1 a 6)
#Párrafo - Texto informativo
#Pregunta simple - Pregunta de respuesta corta
#Pregunta simple* - Pregunta de respuesta corta OBLIGATORIA
#Pregunta larga - Pregunta de respuesta abierta
#Checkbox - Pregunta de selección múltiple
- Opción 1
- Opción 2
#Desplegable - Pregunta de selección única
- Opción 1
- Opción 2
#Archivo - Pide que suban un archivo
#Divisor
#Página     (separa el formulario en pasos)

Reglas: añade * justo después del tipo (ej. #Checkbox* - …) para marcar la pregunta como obligatoria; aplica * solo a las imprescindibles, el resto opcionales. Las opciones van en líneas que empiezan por "- " justo debajo de #Checkbox o #Desplegable. Usa #Título para agrupar secciones y #Página para dividir en pasos si hay más de ~8 preguntas.`;
